import React from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import ActionButtons from "./ActionButtons";

interface SectionWrapperBaseProps {
  title?: string;
  description?: string;
  headingBottomSpacing?: number | string;
  sectionPadding?: number | string;
  onEdit?: () => void;
  saveDisabled?: boolean;
  saveLabel?: string;
  hideCancel?: boolean;
  children: React.ReactNode;
  id?: string;
}

interface SectionWrapperWithButtons extends SectionWrapperBaseProps {
  hideButtons?: false;
  editMode: boolean;
  onCancel: () => void;
  onSave: () => void;
}

interface SectionWrapperWithoutButtons extends SectionWrapperBaseProps {
  hideButtons: true;
  editMode?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
}

type SectionWrapperProps = SectionWrapperWithButtons | SectionWrapperWithoutButtons;

export const SectionWrapper: React.FC<SectionWrapperProps> = ({
  title,
  description,
  headingBottomSpacing = 7,
  sectionPadding,
  editMode,
  onEdit,
  onCancel,
  onSave,
  saveDisabled,
  saveLabel,
  hideCancel,
  hideButtons,
  children,
  id,
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const padding = sectionPadding ?? (isMd ? "0px" : "24px");

  return (
    <Stack
      id={id}
      sx={{
        padding,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: headingBottomSpacing }}
      >
        {(title || description) && (
          <Box sx={{ maxWidth: hideButtons ? "100%" : "80%", flex: 1 }}>
            {title && <Typography variant="h3">{title}</Typography>}
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
        )}
        {!hideButtons && (
          <Box sx={{ flexShrink: 0, ml: 2 }}>
            <ActionButtons
              editMode={editMode as boolean}
              onEdit={onEdit}
              onCancel={onCancel as () => void}
              onSave={onSave as () => void}
              saveDisabled={saveDisabled}
              saveLabel={saveLabel}
              hideCancel={hideCancel}
            />
          </Box>
        )}
      </Stack>
      {children}
    </Stack>
  );
};

export default SectionWrapper;
